import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import { DatePicker, Form } from "rsuite";
import NumberStepperControl from "../../common/NumberStepperControl";
import {
  MAX_LUGGAGE,
  MAX_PASSENGERS,
  MIN_LUGGAGE,
  MIN_PASSENGERS,
} from "../orderFormConstants";
import {
  clampScheduleDateTime,
  getEarliestSchedulableDateTime,
  isScheduleDateTimeInPast,
  joinScheduleDateTime,
  openRsuiteDatePickerTimeView,
  SCHEDULE_DATE_TIME_MENU_CLASS,
  shouldDisableScheduleDate,
  shouldDisableScheduleHour,
  shouldDisableScheduleMinute,
  splitScheduleDateTime,
} from "../scheduleDateTime";
import { OrderFormLocale } from "../types";
import styles from "./orderForm.module.scss";

type ScheduleFieldsProps = {
  locale: OrderFormLocale;
  date: string;
  time: string;
  passengers: number;
  luggage: number;
  onDateChange: (value: string) => void;
  onTimeChange: (value: string) => void;
  onPassengersChange: (value: number) => void;
  onLuggageChange: (value: number) => void;
};

const ScheduleFields: React.FC<ScheduleFieldsProps> = ({
  locale,
  date,
  time,
  passengers,
  luggage,
  onDateChange,
  onTimeChange,
  onPassengersChange,
  onLuggageChange,
}) => {
  const scheduleValue = useMemo(() => joinScheduleDateTime(date, time), [date, time]);

  const calendarDefaultDate = useMemo(() => {
    if (scheduleValue && !isScheduleDateTimeInPast(scheduleValue)) {
      return scheduleValue;
    }
    return getEarliestSchedulableDateTime();
  }, [scheduleValue]);

  const handleDateTimeChange = (value: Date | null) => {
    if (!value) {
      onDateChange("");
      onTimeChange("");
      return;
    }

    const clamped = clampScheduleDateTime(value);
    const next = splitScheduleDateTime(clamped);
    onDateChange(next.date);
    onTimeChange(next.time);
  };

  const handleDateTimeSelect = useCallback(() => {
    requestAnimationFrame(() => {
      const menu = document.querySelector(`.${SCHEDULE_DATE_TIME_MENU_CLASS}`);
      openRsuiteDatePickerTimeView(menu);
    });
  }, []);

  const dateTimeLabel = locale.schedule.dateTimeLabel;

  return (
    <div className={styles.schedWrap}>
      <Form.Group controlId="schedule-datetime" className={styles.schedDateTimeField}>
        <Form.ControlLabel className={styles.schedLabel}>{dateTimeLabel}</Form.ControlLabel>
        <DatePicker
          block
          editable
          size="lg"
          appearance="subtle"
          format="dd.MM.yyyy HH:mm"
          placeholder="Pick date and time"
          value={scheduleValue}
          calendarDefaultDate={calendarDefaultDate}
          menuClassName={`${styles.schedRsuiteMenu} ${SCHEDULE_DATE_TIME_MENU_CLASS}`}
          className={classNames(styles.schedDateTimePicker2, styles.rsControl)}
          preventOverflow
          shouldDisableDate={shouldDisableScheduleDate}
          shouldDisableHour={shouldDisableScheduleHour}
          shouldDisableMinute={shouldDisableScheduleMinute}
          onChange={handleDateTimeChange}
          onSelect={handleDateTimeSelect}
        />
      </Form.Group>

      <p className={styles.schedNote}>{locale.schedule.timezoneNote}</p>

      <div className={styles.schedStepperRow}>
        <div className={styles.schedStepperItem}>
          <NumberStepperControl
            label={locale.schedule.passengersLabel}
            value={passengers}
            min={MIN_PASSENGERS}
            max={MAX_PASSENGERS}
            step={1}
            inputReadOnly
            onChange={onPassengersChange}
            decreaseAriaLabel="Decrease passengers"
            increaseAriaLabel="Increase passengers"
          />
        </div>
        <div className={styles.schedStepperItem}>
          <NumberStepperControl
            label={locale.schedule.luggageLabel}
            value={luggage}
            min={MIN_LUGGAGE}
            max={MAX_LUGGAGE}
            step={1}
            inputReadOnly
            onChange={onLuggageChange}
            decreaseAriaLabel="Decrease luggage"
            increaseAriaLabel="Increase luggage"
          />
        </div>
      </div>
    </div>
  );
};

export default ScheduleFields;
